import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'src/shared/components/ui/dialog';
import { Button } from 'src/shared/components/ui/button';
import RadioGroup from 'src/shared/components/radio-group/RadioGroup';
import type { RiskInsightsRunScope } from 'src/app/portfolio/state/api/riskInsightsRefreshEngineSlice';

export type RunRefreshModalProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    totalSiteCount: number;
    needsSyncCount: number;
    onSubmit: (scope: RiskInsightsRunScope) => void;
    isSubmitting: boolean;
};

const defaultScope = (needsSyncCount: number): RiskInsightsRunScope => (needsSyncCount > 0 ? 'needs_data_refresh' : 'all');

const RunRefreshModal = ({ open, onOpenChange, totalSiteCount, needsSyncCount, onSubmit, isSubmitting }: RunRefreshModalProps) => {
    const [scope, setScope] = useState<RiskInsightsRunScope>(defaultScope(needsSyncCount));

    useEffect(() => {
        if (open) {
            setScope(defaultScope(needsSyncCount));
        }
    }, [open, needsSyncCount]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-w-md gap-4' data-testid='run-refresh-modal'>
                <DialogHeader>
                    <DialogTitle>Refresh risk data</DialogTitle>
                    <DialogDescription className='text-left text-sm text-muted-foreground'>
                        Recalculate the risk insights for this portfolio. Choose all sites after a methodology change; updated sites is
                        enough when only site data has changed.
                    </DialogDescription>
                </DialogHeader>
                <RadioGroup
                    options={[
                        {
                            value: 'needs_data_refresh',
                            label: `Updated sites only (${needsSyncCount})`,
                            isDisabled: needsSyncCount === 0,
                        },
                        { value: 'all', label: `All sites (${totalSiteCount})` },
                    ]}
                    selectedOption={scope}
                    onChange={value => setScope(value as RiskInsightsRunScope)}
                />
                <DialogFooter>
                    <Button variant='outline' type='button' onClick={() => onOpenChange(false)} data-testid='run-refresh-cancel'>
                        Cancel
                    </Button>
                    <Button
                        type='button'
                        className='flex-grow'
                        onClick={() => onSubmit(scope)}
                        disabled={isSubmitting}
                        data-testid='run-refresh-confirm'
                    >
                        {isSubmitting ? 'Starting…' : 'Start run'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default RunRefreshModal;
